/**
 * Wołacz imienia w powitaniach (PLAN §8.4; fala 9, grupa 162): makieta mówi
 * „Cześć, Marto”, a kod wstawiał mianownik („Cześć, Marta”).
 *
 * Słownik obejmuje najczęstsze imiona z publicznego wykazu imion nadanych
 * w rejestrze PESEL (dane.gov.pl); wołacze są opracowane ręcznie. To słownik
 * językowy, nie dane demo [D19]. Imienia spoza słownika nie zgadujemy — reguła
 * końcówek myli się przy imionach obcych — więc zwracamy `null`, a ekran
 * wybiera wtedy powitanie neutralne, bez imienia. To samo przy imieniu
 * złożonym („Anna Maria”) i pustym.
 */

const VOCATIVES: ReadonlyArray<readonly [string, string]> = [
  /* Imiona żeńskie */
  ['Ada', 'Ado'], ['Adrianna', 'Adrianno'], ['Agata', 'Agato'], ['Agnieszka', 'Agnieszko'],
  ['Aleksandra', 'Aleksandro'], ['Alicja', 'Alicjo'], ['Amelia', 'Amelio'], ['Aneta', 'Aneto'],
  ['Angelika', 'Angeliko'], ['Anna', 'Anno'], ['Antonina', 'Antonino'], ['Barbara', 'Barbaro'],
  ['Beata', 'Beato'], ['Bożena', 'Bożeno'], ['Danuta', 'Danuto'], ['Dominika', 'Dominiko'],
  ['Dorota', 'Doroto'], ['Edyta', 'Edyto'], ['Elżbieta', 'Elżbieto'], ['Emilia', 'Emilio'],
  ['Ewa', 'Ewo'], ['Ewelina', 'Ewelino'], ['Gabriela', 'Gabrielo'], ['Grażyna', 'Grażyno'],
  ['Halina', 'Halino'], ['Hanna', 'Hanno'], ['Helena', 'Heleno'], ['Irena', 'Ireno'],
  ['Iwona', 'Iwono'], ['Izabela', 'Izabelo'], ['Jadwiga', 'Jadwigo'], ['Janina', 'Janino'],
  ['Joanna', 'Joanno'], ['Jolanta', 'Jolanto'], ['Józefa', 'Józefo'], ['Julia', 'Julio'],
  ['Justyna', 'Justyno'], ['Karolina', 'Karolino'], ['Katarzyna', 'Katarzyno'], ['Kinga', 'Kingo'],
  ['Klaudia', 'Klaudio'], ['Krystyna', 'Krystyno'], ['Laura', 'Lauro'], ['Lena', 'Leno'],
  ['Magdalena', 'Magdaleno'], ['Maja', 'Majo'], ['Małgorzata', 'Małgorzato'], ['Maria', 'Mario'],
  ['Marianna', 'Marianno'], ['Marta', 'Marto'], ['Martyna', 'Martyno'], ['Marzena', 'Marzeno'],
  ['Milena', 'Mileno'], ['Monika', 'Moniko'], ['Natalia', 'Natalio'], ['Nikola', 'Nikolo'],
  ['Oliwia', 'Oliwio'], ['Patrycja', 'Patrycjo'], ['Paula', 'Paulo'], ['Paulina', 'Paulino'],
  ['Renata', 'Renato'], ['Sandra', 'Sandro'], ['Sylwia', 'Sylwio'], ['Teresa', 'Tereso'],
  ['Urszula', 'Urszulo'], ['Wanda', 'Wando'], ['Weronika', 'Weroniko'], ['Wiktoria', 'Wiktorio'],
  ['Zofia', 'Zofio'], ['Zuzanna', 'Zuzanno'],
  /* Imiona męskie */
  ['Adam', 'Adamie'], ['Adrian', 'Adrianie'], ['Aleksander', 'Aleksandrze'], ['Andrzej', 'Andrzeju'],
  ['Antoni', 'Antoni'], ['Arkadiusz', 'Arkadiuszu'], ['Artur', 'Arturze'], ['Bartłomiej', 'Bartłomieju'],
  ['Bartosz', 'Bartoszu'], ['Damian', 'Damianie'], ['Daniel', 'Danielu'], ['Dariusz', 'Dariuszu'],
  ['Dawid', 'Dawidzie'], ['Dominik', 'Dominiku'], ['Filip', 'Filipie'], ['Franciszek', 'Franciszku'],
  ['Grzegorz', 'Grzegorzu'], ['Hubert', 'Hubercie'], ['Igor', 'Igorze'], ['Jacek', 'Jacku'],
  ['Jakub', 'Jakubie'], ['Jan', 'Janie'], ['Janusz', 'Januszu'], ['Jarosław', 'Jarosławie'],
  ['Jerzy', 'Jerzy'], ['Józef', 'Józefie'], ['Kacper', 'Kacprze'], ['Kamil', 'Kamilu'],
  ['Karol', 'Karolu'], ['Kazimierz', 'Kazimierzu'], ['Konrad', 'Konradzie'], ['Krystian', 'Krystianie'],
  ['Krzysztof', 'Krzysztofie'], ['Leszek', 'Leszku'], ['Łukasz', 'Łukaszu'], ['Maciej', 'Macieju'],
  ['Maksymilian', 'Maksymilianie'], ['Marcin', 'Marcinie'], ['Marek', 'Marku'], ['Mariusz', 'Mariuszu'],
  ['Mateusz', 'Mateuszu'], ['Michał', 'Michale'], ['Mikołaj', 'Mikołaju'], ['Mirosław', 'Mirosławie'],
  ['Norbert', 'Norbercie'], ['Oskar', 'Oskarze'], ['Patryk', 'Patryku'], ['Paweł', 'Pawle'],
  ['Piotr', 'Piotrze'], ['Przemysław', 'Przemysławie'], ['Rafał', 'Rafale'], ['Robert', 'Robercie'],
  ['Ryszard', 'Ryszardzie'], ['Sebastian', 'Sebastianie'], ['Stanisław', 'Stanisławie'],
  ['Stefan', 'Stefanie'], ['Szymon', 'Szymonie'], ['Tadeusz', 'Tadeuszu'], ['Tomasz', 'Tomaszu'],
  ['Wiktor', 'Wiktorze'], ['Wojciech', 'Wojciechu'], ['Zbigniew', 'Zbigniewie'], ['Zenon', 'Zenonie'],
]

const BY_NAME = new Map(VOCATIVES.map(([nominative, vocative]) => [nominative.toLocaleLowerCase('pl-PL'), vocative]))

/** Zachowuje wielkość liter wejścia: „ANNA” → „ANNO”, „anna” → „anno”. */
function matchCase(source: string, vocative: string): string {
  if (source === source.toLocaleUpperCase('pl-PL')) return vocative.toLocaleUpperCase('pl-PL')
  if (source === source.toLocaleLowerCase('pl-PL')) return vocative.toLocaleLowerCase('pl-PL')
  return vocative
}

/** Wołacz imienia albo `null`, gdy imienia nie znamy (wtedy powitanie neutralne). */
export function vocativePL(firstName: string | null | undefined): string | null {
  const name = firstName?.trim() ?? ''
  if (name === '' || /\s|-/.test(name)) return null
  const vocative = BY_NAME.get(name.toLocaleLowerCase('pl-PL'))
  return vocative === undefined ? null : matchCase(name, vocative)
}
